import { useState } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const TENANT_ID = process.env.REACT_APP_TENANT_ID?.trim() || undefined;

const Login = ({ apiBaseUrl, onSuccess }) => {
  const { toast } = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!username || !password || isSubmitting) {
      if (!isSubmitting) {
        console.warn('[SimpleAuth] Login blocked: username or password is empty.');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const { data } = await axios.post(
        `${apiBaseUrl}/auth/login`,
        {
          username,
          password,
          tenantId: TENANT_ID
        },
        // Сессию сервер кладёт в HttpOnly-cookie; без флага браузер отверг бы её, когда api на другом адресе.
        { withCredentials: true }
      );
      console.info('[SimpleAuth] Login succeeded.', {
        username,
        role: data?.user?.role ?? 'unknown'
      });

      onSuccess?.(data?.user ?? null);

      toast({
        title: 'Добро пожаловать!',
        description: 'Авторизация прошла успешно.'
      });
    } catch (error) {
      const isServerUnavailable = axios.isAxiosError(error) && !error.response;
      const message = isServerUnavailable
        ? 'Сервер недоступен. Проверьте подключение и повторите попытку.'
        : error?.response?.data?.message ?? 'Не удалось выполнить вход. Проверьте логин и пароль.';

      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.error('[SimpleAuth] Login failed with server response.', {
            username,
            status: error.response.status,
            statusText: error.response.statusText,
            message: error.response?.data?.message ?? error.message
          });
        } else {
          console.error('[SimpleAuth] Login failed: server is unavailable or network error.', {
            username,
            message: error.message
          });
        }
      } else {
        console.error('[SimpleAuth] Login failed due to unexpected client error.', {
          username,
          error
        });
      }

      toast({
        title: 'Ошибка входа',
        description: message,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-md shadow-lg">
      <CardHeader>
        <CardTitle>Вход в систему</CardTitle>
        <CardDescription>Введите логин и пароль, чтобы перейти к панели управления.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="username">Логин</Label>
            <Input
              id="username"
              data-ev="login.username"
              type="text"
              value={username}
              placeholder="Введите логин"
              autoComplete="username"
              onChange={(event) => setUsername(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Пароль</Label>
            <Input
              id="password"
              data-ev="login.password"
              type="password"
              value={password}
              placeholder="Введите пароль"
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          <Button className="w-full" type="submit" data-ev="login.submit" disabled={isSubmitting}>
            {isSubmitting ? 'Выполняется вход...' : 'Войти'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default Login;
